/**
 * **THE CLUBS PORTAL IN SPANISH** (Spanish for 3.0.11; Evan, 28 Sep: "the clubs portal opens in the browser's language
 * … the admin site stays English").
 *
 * - `ES_ROWS` (esRows.ts, generated): the reviewed sheet's wording for the deck's plain strings.
 * - `ES_TEMPLATES` below: the deck's templates, each written from its reviewed rows — the sheet shows a template's
 *   branches as `key#one`, `key#noGroup`… and each branch here says which row it is. Templates that only arrange
 *   values with no words (`roster.chipCount`, `roster.pct`, `phone.cardTitle`…) have no Spanish of their own.
 *   The admin's areas (O11, O13, O14, Village Square, Help, Metrics) have none either: the admin site stays English.
 * - `ES_DRAFTS`: the portal's stand-in lines (placeholders.ts) and the switch's accessible label, first drafted here and
 *   reviewed in the US pass (30 Sep) as docs/handoff/spanish/portal-311.csv (the test keeps them equal).
 *
 * Product names stay English (the glossary). "English" and "Español" are each language's own name and never change.
 */
import { BAND_ORDER, bandLabel, type ScoreBand } from '@kk/domain';

import type { StaffRole } from '@/types/wire';

const isScoreBand = (b: string): b is ScoreBand => (BAND_ORDER as readonly string[]).includes(b);

type Template = (...args: never[]) => string;
const childCount = (n: number) => (n === 1 ? `${n} niño` : `${n} niños`); // o05.childCount / #one

export const ES_TEMPLATES: Readonly<Record<string, Template>> = {
  'shell.groupAdmin': ((g?: string) => (g ? `Admin. de grupo · ${g}` : 'Admin. de grupo')) as Template, // #noGroup
  'approvals.title': ((n: number) => `Aprobaciones · ${n} en espera`) as Template,
  'approvals.approveAll': ((n: number) => `Aprobar las ${n}`) as Template,
  'roster.title': ((n: number) => `Lista de niños · ${n} niños`) as Template,
  'roster.chipAll': ((n: number) => `Todos (${n})`) as Template,
  'roster.chipPending': ((n: number) => `Consentimiento pendiente · ${n}`) as Template,
  'roster.ofTotal': ((done: number, total: number) => `${done} de ${total}`) as Template,
  'roster.invited': ((email: string) => `Invitado · ${email}`) as Template,
  'redemptions.codeValid': ((code: string) => `CÓDIGO ${code} · VÁLIDO`) as Template,
  'redemptions.waiting': ((n: number) => `EN ESPERA · ${n}`) as Template,
  'redemptions.requested': ((relativeDay: string, code: string) => `Solicitado ${relativeDay} · ${code}`) as Template,
  'redemptions.fulfilledThisMonth': ((n: number) => `Entregados este mes · ${n} ·`) as Template,
  'o03.publish': ((n: number) => (n === 1 ? `Publicar para ${n} niño` : `Publicar para ${n} niños`)) as Template, // #one
  'o03.tasks': ((n: number) => `TAREAS · ${n}`) as Template,
  'o03.reps': ((n: number) => (n === 1 ? '1 repetición' : `${n} repeticiones`)) as Template, // #one
  // the band is a wire value ("Good"); Spanish names it as the app does (@kk/domain's bandLabel, from the glossary)
  'o03.band': ((band: string) => `· ${isScoreBand(band) ? bandLabel(band, 'es') : band}`) as Template,
  'o03.preview': ((firstName: string) => `ASÍ LO VE ${firstName.toUpperCase()}`) as Template,
  'o03.previewLine': ((title: string, staffName: string) => `${title} · aprueba ${staffName}`) as Template,
  'o18.thanks': ((name: string) => `Gracias, ${name}. Nos pondremos en contacto en unos días.`) as Template,
  'o18.referredBy': ((village: string) => `Por invitación de ${village}`) as Template,
  'o18.approved': ((club: string) => `${club} ya está configurado y su director recibió el correo.`) as Template,
  'o17.askAnOwnerBody': ((email: string) =>
    `Iniciaste sesión como ${email}. Ya avisamos a los propietarios y uno de ellos elegirá tu rol.`) as Template,
  'o17.endsOnShown': ((date: string) => `El acceso termina el ${date}`) as Template,
  'o17.confirmRemove': ((name: string) => `¿Quitar a ${name}? Perderá el acceso de inmediato.`) as Template,
  'o17.confirmBody': ((email: string) =>
    `Enviamos un código de 6 dígitos a ${email}. Ingrésalo para unirte al equipo.`) as Template,
  'o06.adventureEyebrow': ((order: number, title: string) => `AVENTURA ${order} · ${title.toUpperCase()}`) as Template,
  'o06.missionEyebrow': ((order: number, title: string) => `Misión ${order} · ${title}`) as Template,
  'o06.quizItems': ((n: number) => `PREGUNTAS DEL CUESTIONARIO · ${n}`) as Template,
  'o06.syncResult': ((checked: number, flagged: number) =>
    checked === 1
      ? `${checked} misión revisada · ${flagged} marcadas` // #one
      : `${checked} misiones revisadas · ${flagged} marcadas`) as Template,
  'o06.status': ((s: 'empty' | 'draft' | 'review' | 'live' | 'retired') =>
    ({ empty: 'Vacío', draft: 'Borrador', review: 'En revisión', live: 'Publicado', retired: 'Retirado' })[s]) as Template,
  'o09.sendCta': ((n: number) => (n === 1 ? `Enviar ${n} invitación` : `Enviar ${n} invitaciones`)) as Template, // #one
  'o09.state': ((s: 'invited' | 'joined' | 'expired') =>
    ({ invited: 'Invitado', joined: 'Unido', expired: 'Vencida' })[s]) as Template,
  'o08.staff': ((n: number) => `PERSONAL · ${n}`) as Template,
  'o08.roleValue': ((role: StaffRole, groups: number, you: boolean) =>
    role === 'orgAdmin'
      ? you
        ? 'Admin. de la organización · tú' // .orgAdminYou
        : 'Admin. de la organización' // .orgAdmin
      : groups > 0
        ? 'Admin. de grupo' // .groupAdmin
        : 'Personal') as Template, // .staff
  'o08.children': childCount as Template, // #one
  'o08.expires': ((date: string) => `vence el ${date}`) as Template,
  'o05.childCount': childCount as Template, // #one
  'o05.meta': ((children: number, staff: number, names: string[]) =>
    [childCount(children), `${staff} del personal`, ...(names.length ? [names.join(', ')] : [])].join(' · ')) as Template,
  'o05.codeLine': ((expires?: string, left?: number, max?: number) =>
    [
      expires ? `Vence el ${expires}` : undefined, // .expires
      left !== undefined && max !== undefined
        ? left === 1
          ? `Queda ${left} de ${max} usos` // .uses#one
          : `Quedan ${left} de ${max} usos` // .uses
        : undefined,
    ]
      .filter(Boolean)
      .join(' · ')) as Template,
  'o05.info.branchesText': ((n: number) =>
    `· esta organización tiene ${n} ${n === 1 ? 'sede' : 'sedes'}.`) as Template,
  'phone.approvalsSegment': ((n: number) => `Aprobaciones · ${n}`) as Template,
  'phone.redemptionsSegment': ((n: number) => `Canjes · ${n}`) as Template,
  'states.notKkStaff.body': ((email: string) =>
    `Iniciaste sesión como ${email}, que no es una cuenta del equipo de Kiddie Kredit. Si trabajas con un club, usa clubs.kiddiekredit.com.`) as Template,
  'states.inviteWrongAccount.body': ((currentEmail: string) =>
    `Iniciaste sesión como ${currentEmail}. Cierra sesión y vuelve a abrir el enlace con la cuenta a la que se envió.`) as Template,
  'errors.forbidden': ((orgName?: string) =>
    orgName
      ? `Esa página es para administradores de la organización. Si necesitas acceso, pídeselo a un administrador de ${orgName}.`
      : 'Esa página es para administradores de la organización. Si necesitas acceso, pídeselo a un administrador.') as Template, // #noOrg
  'signIn.code.resendIn': ((seconds: number) =>
    `Enviar un código nuevo en ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`) as Template,
  'signIn.confirmTakeDown': ((title: string) =>
    `¿Quitar ${title} del recorrido ahora? Los niños dejarán de verlo de inmediato.`) as Template,
};

/**
 * **REVIEWED in the US pass (30 Sep; docs/handoff/spanish/portal-311.csv), wording unchanged** — first drafted here. Keys are `placeholders.*` (the
 * portal's stand-in lines, which never went to the sheet) and `deck.language.label` (the switch's accessible label,
 * PROPOSED in English too). Wording follows the reviewed portal terms: rechazar, Cancelar, Cerrar sesión, Asignaciones,
 * Tarea, Quitar, Sedes, Vencida, and "puntaje" for a bare score.
 */
export const ES_DRAFTS: Readonly<Record<string, { spanish: string; note?: string }>> = {
  'deck.language.label': { spanish: 'Idioma', note: 'the switch’s accessible label (a screen reader says it); PROPOSED in English too' },
  'placeholders.signIn.password': { spanish: 'Contraseña' },
  'placeholders.signIn.signOut': { spanish: 'Cerrar sesión' },
  'placeholders.signIn.orgPicker': { spanish: 'Organización' },
  'placeholders.sheet.close': { spanish: 'Cerrar' },
  'placeholders.sheet.menu': { spanish: 'Menú' },
  'placeholders.deny.title': { spanish: 'Motivo' },
  'placeholders.deny.notePlaceholder': { spanish: 'Nota opcional' },
  'placeholders.deny.confirm': { spanish: 'Rechazar' },
  'placeholders.deny.cancel': { spanish: 'Cancelar' },
  'placeholders.deny.approveA11y': { spanish: 'Aprobar' },
  'placeholders.deny.denyA11y': { spanish: 'Rechazar' },
  'placeholders.proof.noteTitle': { spanish: 'Nota' },
  'placeholders.proof.photoTitle': { spanish: 'Foto' },
  'placeholders.proof.photoAlt': { spanish: 'Foto de prueba', note: 'the photo a child sent as proof' },
  'placeholders.proof.photoExpired': { spanish: 'Esta foto ya venció' },
  'placeholders.empty.approvals': { spanish: 'No hay nada en espera' },
  'placeholders.empty.waiting': { spanish: 'No hay nadie en espera' },
  'placeholders.empty.loading': { spanish: 'Cargando…' },
  'placeholders.invite.accepting': { spanish: 'Aceptando tu invitación…' },
  'placeholders.redemptions.statusWord.fulfilled': { spanish: 'USADO', note: 'a reward code: USED' },
  'placeholders.redemptions.statusWord.denied': { spanish: 'RECHAZADO' },
  'placeholders.redemptions.statusWord.expired': { spanish: 'VENCIDO' },
  'placeholders.redemptions.statusWord.cancelled': { spanish: 'CANCELADO' },
  'placeholders.redemptions.codeStatus': { spanish: 'CÓDIGO {code} · {word}' },
  'placeholders.redemptions.codeField': { spanish: 'Código' },
  'placeholders.assignments.newAssignment': { spanish: 'Nueva asignación' },
  'placeholders.assignments.columns.assignment': { spanish: 'ASIGNACIÓN' },
  'placeholders.assignments.columns.tasks': { spanish: 'TAREAS' },
  'placeholders.assignments.columns.children': { spanish: 'NIÑOS' },
  'placeholders.assignments.columns.status': { spanish: 'ESTADO' },
  'placeholders.assignments.empty': { spanish: 'Todavía no hay asignaciones' },
  'placeholders.builder.titleField': { spanish: 'Título' },
  'placeholders.builder.startDate': { spanish: 'Inicio' },
  'placeholders.builder.endDate': { spanish: 'Fin' },
  'placeholders.builder.taskTitle': { spanish: 'Tarea' },
  'placeholders.builder.fewerReps': { spanish: 'Menos' },
  'placeholders.builder.moreReps': { spanish: 'Más' },
  'placeholders.builder.remove': { spanish: 'Quitar' },
  'placeholders.builder.minScore': { spanish: 'Puntaje mínimo', note: 'the glossary: a bare "score" is "puntaje"' },
  'placeholders.builder.saved': { spanish: 'Guardado' },
  'placeholders.builder.readOnly': { spanish: 'Publicada · solo lectura' },
  'placeholders.settings.save': { spanish: 'Guardar' },
  'placeholders.groups.orgAdminOnly': { spanish: 'Solo para administradores de la organización' },
  'placeholders.groups.empty': { spanish: 'Todavía no hay grupos' },
  'placeholders.groups.nameField': { spanish: 'Nombre' },
  'placeholders.groups.branchField': { spanish: 'Sede' },
  'placeholders.groups.noBranch': { spanish: 'Sin sede' },
  'placeholders.staff.invite': { spanish: 'Invitar' },
  'placeholders.staff.role': { spanish: 'Rol' },
  'placeholders.staff.columns.name': { spanish: 'NOMBRE' },
  'placeholders.staff.columns.role': { spanish: 'ROL' },
  'placeholders.staff.columns.sent': { spanish: 'ENVIADA', note: 'the date an invite was sent (la invitación)' },
  'placeholders.staff.pending': { spanish: 'Pendiente' },
  'placeholders.staff.empty': { spanish: 'Todavía no hay personal' },
};

/** The drafted templates among the placeholders, as code (their CSV rows show `{name}`). */
export const ES_DRAFT_TEMPLATES: Readonly<Record<string, Template>> = {
  'placeholders.redemptions.codeStatus': ((code: string, word: string) => `CÓDIGO ${code} · ${word}`) as Template,
};

/**
 * **THE 3.1.0 LINES** (docs/handoff/spanish/portal-310.csv, reviewed by Evan, 9 Oct 2026): 03c's Category select in
 * the assignment builder. Kept apart from `ES_DRAFTS`, which is what `portal-311.csv` is written from. The test holds
 * this table to the sheet's reviewed rows, word for word.
 */
export const ES_310: Readonly<Record<string, string>> = {
  'deck.o03.categoryHelp': 'Cada tarea necesita una categoría para el Kredit Score. Ámbar quiere decir que no pudimos saberla por el título.',
  'deck.o03.category': 'Categoría',
  'deck.o03.categoryNeeded': 'Elige una categoría para cada tarea en ámbar para publicar.',
  'deck.o03.bandStarting': 'Empezando',
  'deck.nav.clubGuide': 'Guía para clubes (PDF)',
};

/**
 * **THE 23 OCT AUDIT'S LINES, IN SPANISH** (docs/handoff/drafts/audit-2026-10-23/portal.md; DECISIONS 93). Mike
 * approved the English and Evan the Spanish on 23 Oct. `scripts/portal-1023.ts` writes them to
 * docs/handoff/spanish/portal-1023.csv, every row `reviewed`, and src/copy/esTables.test.ts keeps the two equal.
 *
 * Templates are written as the sheet shows them, each parameter as `{name}`; `spanishFor` fills them in order.
 */
export const ES_1023: Readonly<Record<string, { spanish: string; note?: string }>> = {
  'deck.approvals.subtitle': {
    spanish: 'Tareas terminadas que esperan tu visto bueno.',
    note: 'O01 subtitle; replaces "Anything a club sends you, in one place." (T3-6)',
  },
  'deck.o03.previewNote': {
    spanish:
      'Las tareas del club aparecen en la lista del niño o la niña con la insignia de tu club. Los prelectores escuchan “{shortName}” antes del nombre de la tarea.',
    note: 'O03 preview; {shortName} is the club’s short name (T1-7, T3-4)',
  },
  'deck.o05.info.staffText': {
    spanish:
      '· Invita a personas por correo. Los admin. de grupo aprueban tareas y entregan recompensas en sus grupos. Los admin. de la organización pueden hacer todo lo de aquí.',
    note: 'O05 info card, after the bold "Personal" (T3-8)',
  },
  'deck.o05.info.codesText': {
    spanish:
      '· 8 caracteres, un número fijo de usos y una fecha de vencimiento. La madre o el padre que lo escribe ve el nombre de tu club antes de aceptar.',
    note: 'O05 info card, after the bold "Códigos"; replaces the line with the screen id P19 (T3-8)',
  },
  'deck.o05.nameNeeded': { spanish: 'Escribe un nombre para el grupo.', note: 'New group with an empty name (T3-1)' },
  'deck.o05.emailNeeded': { spanish: 'Escribe una dirección de correo.', note: 'Invite staff without an address (T3-1)' },
  'deck.o09.notAnEmail': { spanish: '{address} no es un correo electrónico.', note: 'O09, a bad address in the list (T3-2)' },
  'deck.states.inviteIncomplete': {
    spanish: 'Este enlace no está completo. Vuelve a abrir la invitación desde tu correo.',
    note: 'an invite link with no token; its button is "Iniciar sesión" (T3-7)',
  },
  'deck.o18.price': {
    spanish: '$5 por niño al mes, o $4 si pagas al año. Solo pagas por los niños cuyas familias se unen.',
    note: 'O18 Apply, under the intro; the Organizations page’s approved price (T3-11)',
  },
  'deck.o18.optional': { spanish: '(opcional)', note: 'O18 Apply, after "Sitio web" (T3-11)' },
  'placeholders.assignments.upcoming': { spanish: 'Próxima', note: 'an assignment (feminine) whose dates have not begun (T3-12)' },
  'placeholders.assignments.running': { spanish: 'En curso', note: 'an assignment inside its dates (T3-12)' },
  'placeholders.assignments.ended': { spanish: 'Terminada', note: 'an assignment (feminine) past its end date (T3-12)' },
  'placeholders.billing.codeNeedsBilling': {
    spanish: 'Este código empieza a funcionar cuando se configure la facturación.',
    note: 'under a dimmed join code, no billing yet (T3-3)',
  },
  'placeholders.billing.codePaused': {
    spanish: 'Este código está en pausa hasta que la facturación esté al día.',
    note: 'under a dimmed join code, club paused (T3-3)',
  },
  'placeholders.billing.pausedAskAdmin': {
    spanish: 'Tu club está en pausa. Pide al administrador de tu club que actualice la facturación.',
    note: 'a group admin on a paused club (T3-9)',
  },
};
